/**
 * Analytics — Event tracking via dataLayer (GA4 / GTM compatible).
 *
 * GTM is loaded by the inline script in index.html, which reads VITE_GTM_ID
 * from window.__ENV__ (injected by env-config.js before the app bundle).
 * When VITE_GTM_ID is empty (local dev), no GTM script is loaded and events
 * are buffered in window.dataLayer without being forwarded anywhere.
 *
 * Event categories:
 * - `funnel_*` — funnel experience events (offers, popups, leads)
 * - `purchase` / `begin_checkout` — GA4 Enhanced Ecommerce events
 */

// ── GA4 Enhanced Ecommerce Events ──────────────────────────────────────────

export interface EcommerceItem {
  item_id: string;
  item_name?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
}

const TRACKED_ORDER_KEY = 'te_last_tracked_order_id';

/**
 * GA4 `purchase` event — fires once per order.
 * Deduplicates via sessionStorage so repeated returns to /checkout-return
 * or bfcache restorations don't double-count.
 */
export function trackPurchase(params: {
  orderId: string;
  value?: number;
  currency?: string;
  items?: EcommerceItem[];
}): void {
  if (typeof window === 'undefined') return;

  const lastTracked = sessionStorage.getItem(TRACKED_ORDER_KEY);
  if (lastTracked === params.orderId) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'purchase',
    ecommerce: {
      transaction_id: params.orderId,
      value: params.value,
      currency: params.currency,
      items: params.items ?? [],
    },
  });

  sessionStorage.setItem(TRACKED_ORDER_KEY, params.orderId);
}

/** GA4 `begin_checkout` event — fired when user is redirected to external checkout. */
export function trackCheckoutStarted(params: {
  cartToken: string;
  value?: number;
  currency?: string;
  itemCount?: number;
}): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'begin_checkout',
    ecommerce: {
      cart_token: params.cartToken,
      value: params.value,
      currency: params.currency,
      item_count: params.itemCount,
    },
  });
}

// ── Funnel Events ──────────────────────────────────────────────────────────

interface FunnelEventParams {
  funnel_id?: string;
  step_id?: string;
  step_type?: string;
  step_name?: string;
  placement?: string;
  experience_type?: string;
  offer_id?: string;
  offer_name?: string;
  product_id?: string;
  variant_id?: string;
  discount_type?: string;
  discount_value?: number;
  action_type?: string;
  trigger_lifecycle?: string;
  [key: string]: string | number | boolean | undefined;
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

function pushEvent(eventName: string, params: FunnelEventParams): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...params,
  });
}

/** Funnel experience rendered on the page. */
export function trackFunnelExperienceShown(params: {
  funnelId: string;
  stepId: string;
  stepName?: string;
  experienceType?: string;
  placement?: string;
}): void {
  pushEvent('funnel_experience_shown', {
    funnel_id: params.funnelId,
    step_id: params.stepId,
    step_name: params.stepName,
    experience_type: params.experienceType,
    placement: params.placement,
  });
}

/** User accepted a funnel offer. */
export function trackFunnelOfferAccepted(params: {
  funnelId: string;
  stepId: string;
  offerId?: string;
  offerName?: string;
  productId?: string;
}): void {
  pushEvent('funnel_offer_accepted', {
    funnel_id: params.funnelId,
    step_id: params.stepId,
    offer_id: params.offerId,
    offer_name: params.offerName,
    product_id: params.productId,
  });
}

/** User declined a funnel offer. */
export function trackFunnelOfferDeclined(params: {
  funnelId: string;
  stepId: string;
  offerId?: string;
}): void {
  pushEvent('funnel_offer_declined', {
    funnel_id: params.funnelId,
    step_id: params.stepId,
    offer_id: params.offerId,
  });
}

/** Product added to cart from a funnel experience. */
export function trackFunnelAddToCart(params: {
  productId: string;
  variantId?: string;
  quantity: number;
  source: 'funnel_popup' | 'funnel_offer' | 'funnel_free_gift';
}): void {
  pushEvent('funnel_add_to_cart', {
    product_id: params.productId,
    variant_id: params.variantId,
    quantity: params.quantity,
    source: params.source,
  });
}

/** Popup shown (leads, free gift, or display offer). */
export function trackFunnelPopupShown(params: {
  stepId: string;
  actionType: string;
  actionId: string;
}): void {
  pushEvent('funnel_popup_shown', {
    step_id: params.stepId,
    action_type: params.actionType,
    action_id: params.actionId,
  });
}

/** Leads popup email submitted. */
export function trackFunnelLeadsSubmitted(params: {
  stepId: string;
  actionId: string;
}): void {
  pushEvent('funnel_leads_submitted', {
    step_id: params.stepId,
    action_id: params.actionId,
  });
}

/** Funnel experience dismissed by user. */
export function trackFunnelExperienceDismissed(params: {
  funnelId: string;
  stepId: string;
}): void {
  pushEvent('funnel_experience_dismissed', {
    funnel_id: params.funnelId,
    step_id: params.stepId,
  });
}
